import React from 'react'
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import {
    Box,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
    Alert,
  } from '@mui/material';
import CustomButton from '../@extended/CustomButton';
import Phone, { usPhoneValidationSchema } from './Phone';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { formatCountryOption, getCountryByCode } from '../../constants/iso3166Countries';
import {
  formatUsStateOption,
  getUsStateByCode,
  US_COUNTRY_CODE,
  US_ZIP_CODE_REGEX,
  usStateCodes,
  usStates,
} from '../../constants/usStates';
import { openMessage } from '../../store/slices/showMessageSlice';
import { sanitizeApiErrorMessage } from '../../util/util';

const validationSchema = Yup.object().shape({
  individual: Yup.object().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    phone: usPhoneValidationSchema.concat(Yup.string().required('Contact Number is required')),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    dob: Yup.mixed()
      .nullable()
      .required('Date of Birth is required')
      .test('valid-dob', 'Date of Birth is required', (value) => {
        if (!value) return false;
        const date = dayjs.isDayjs(value) ? value : dayjs(value);
        return date.isValid() && date.isBefore(dayjs(), 'day');
      }),
    address: Yup.object().shape({
      city: Yup.string().required('City is required'),
      country: Yup.string()
        .required('Country is required')
        .oneOf([US_COUNTRY_CODE], 'Address must be in the United States'),
      line1: Yup.string().required('Address Line 1 is required'),
      line2: Yup.string(), // Line 2 is optional
      postalCode: Yup.string()
        .required('Postal Code is required')
        .matches(US_ZIP_CODE_REGEX, 'Enter a valid US ZIP code (e.g. 12345 or 12345-6789)'),
      state: Yup.string()
        .required('State is required')
        .oneOf(usStateCodes, 'Please select a supported US state'),
    }),
    id_number: Yup.string()
      .transform((value) => (value ? value.replace(/\D/g, '') : ''))
      .length(9, 'Social Security Number must be 9 digits')
      .matches(/^\d{9}$/, 'Social Security Number must contain only digits')
      .required('Social Security Number is required'),
  }),
  bankDetails: Yup.object().shape({
    country: Yup.string()
      .required('Country is required')
      .matches(/^[A-Z]{2}$/, 'Country must be a 2-letter ISO 3166-1 alpha-2 code'),
    currency: Yup.string().required('Currency is required'),
    accountHolderType: Yup.string()
      .oneOf(['company', 'individual'], 'Account Holder Type must be Company or Individual')
      .required('Account Holder Type is required'),
    routingNumber: Yup.string()
      .required('Routing Number is required')
      .matches(/^\d{9}$/, 'Routing Number must be 9 digits'),
    accountNumber: Yup.string()
      .required('Account Number is required')
      .matches(/^\d+$/, 'Account Number must contain only digits'),
    confirmAccountNumber: Yup.string()
      .required('Confirm Account Number is required')
      .matches(/^\d+$/, 'Account Number must contain only digits')
      .test('accountNumbersMatch', 'Account Numbers must match', function (value) {
        return value === this.parent.accountNumber;
      }),
    
  }),
});


const bankCountries = [
  { name: 'United States', code: 'US', currency: 'USD' },
  { name: 'Australia', code: 'AU', currency: 'AUD' },
  { name: 'India', code: 'IN', currency: 'INR' },
  { name: 'Japan', code: 'JP', currency: 'JPY' },
  { name: 'Austria', code: 'AT', currency: 'EUR' },
  { name: 'Belgium', code: 'BE', currency: 'EUR' },
  { name: 'Brazil', code: 'BR', currency: 'BRL' },
  { name: 'Bulgaria', code: 'BG', currency: 'BGN' },
  { name: 'Canada', code: 'CA', currency: 'CAD' },
  { name: 'Croatia', code: 'HR', currency: 'EUR' },
  { name: 'Cyprus', code: 'CY', currency: 'EUR' },
  { name: 'Czech Republic', code: 'CZ', currency: 'CZK' },
  { name: 'Denmark', code: 'DK', currency: 'DKK' },
  { name: 'Estonia', code: 'EE', currency: 'EUR' },
  { name: 'Finland', code: 'FI', currency: 'EUR' },
  { name: 'France', code: 'FR', currency: 'EUR' },
  { name: 'Germany', code: 'DE', currency: 'EUR' },
  { name: 'Ghana', code: 'GH', currency: 'GHS' },
  { name: 'Gibraltar', code: 'GI', currency: 'GIP' },
  { name: 'Greece', code: 'GR', currency: 'EUR' },
  { name: 'Hong Kong', code: 'HK', currency: 'HKD' },
  { name: 'Hungary', code: 'HU', currency: 'HUF' },
  { name: 'Indonesia', code: 'ID', currency: 'IDR' },
  { name: 'Ireland', code: 'IE', currency: 'EUR' },
  { name: 'Italy', code: 'IT', currency: 'EUR' },
  { name: 'Kenya', code: 'KE', currency: 'KES' },
  { name: 'Latvia', code: 'LV', currency: 'EUR' },
  { name: 'Liechtenstein', code: 'LI', currency: 'CHF' },
  { name: 'Lithuania', code: 'LT', currency: 'EUR' },
  { name: 'Luxembourg', code: 'LU', currency: 'EUR' },
  { name: 'Malaysia', code: 'MY', currency: 'MYR' },
  { name: 'Malta', code: 'MT', currency: 'EUR' },
  { name: 'Mexico', code: 'MX', currency: 'MXN' },
  { name: 'Netherlands', code: 'NL', currency: 'EUR' },
  { name: 'New Zealand', code: 'NZ', currency: 'NZD' },
  { name: 'Nigeria', code: 'NG', currency: 'NGN' },
  { name: 'Norway', code: 'NO', currency: 'NOK' },
  { name: 'Poland', code: 'PL', currency: 'PLN' },
  { name: 'Portugal', code: 'PT', currency: 'EUR' },
  { name: 'Romania', code: 'RO', currency: 'RON' },
  { name: 'Singapore', code: 'SG', currency: 'SGD' },
  { name: 'Slovakia', code: 'SK', currency: 'EUR' },
  { name: 'Slovenia', code: 'SI', currency: 'EUR' },
  { name: 'South Africa', code: 'ZA', currency: 'ZAR' },
  { name: 'Spain', code: 'ES', currency: 'EUR' },
  { name: 'Sweden', code: 'SE', currency: 'SEK' },
  { name: 'Switzerland', code: 'CH', currency: 'CHF' },
  { name: 'Thailand', code: 'TH', currency: 'THB' },
  { name: 'United Arab Emirates', code: 'AE', currency: 'AED' },
  { name: 'United Kingdom', code: 'GB', currency: 'GBP' },
];

export default function BankDetailsForm({handleClose, handleSave, isLoading, isCompany }) {
  const dispatch = useDispatch();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
      individual: {
        firstName: values.individual.firstName,
        lastName: values.individual.lastName,
        phone: values.individual.phone,
        email: values.individual.email,
        dob: {
          day: values.individual?.dob.date(),
          month: values.individual?.dob.month() + 1,
          year: values.individual?.dob.year(),
        },
        address: {
          city: values.individual.address.city,
          country: US_COUNTRY_CODE,
          line1: values.individual.address.line1,
          line2: values.individual.address.line2,
          postal_code: values.individual.address.postalCode,
          state: values.individual.address.state,
        },
        id_number: values.individual.id_number.replace(/\D/g, ''),
      },
      bankDetails: {
        country: values.bankDetails.country,
        currency: values.bankDetails.currency,
        accountHolderName: `${values.individual.firstName} ${values.individual.lastName}`,
        accountHolderType: values.bankDetails.accountHolderType,
        accountNumber: values.bankDetails.accountNumber,
        confirmAccountNumber: values.bankDetails.confirmAccountNumber,
        routingNumber: values.bankDetails.routingNumber,
        swiftNumber: values.bankDetails.swiftNumber,
      },
      ...(isCompany && { company: { name: values.company.name, taxId: values.company.taxId } }),
      };

      await handleSave(payload);
    } catch (error) {
      dispatch(
        openMessage({
          message: sanitizeApiErrorMessage(error),
          messageSeverity: 'error',
        })
      );
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Formik
      initialValues={{
        individual: {
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          dob: null,
          address: {
            city: '',
            country: US_COUNTRY_CODE,
            line1: '',
            line2: '',
            postalCode: '',
            state: '',
          },
          id_number:''
        },
        bankDetails: {
          country: US_COUNTRY_CODE,
          currency: 'USD',
          accountHolderName: '',
          accountHolderType: '',
          routingNumber: '',
          accountNumber: '',
          confirmAccountNumber: '',
        },
        ...(isCompany && { company: { name: "", taxId: "" } }),
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ isSubmitting, touched, errors, handleChange, values, setFieldValue, submitCount, isValid }) => (
        <Form>
          {submitCount > 0 && !isValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the highlighted errors before submitting.
            </Alert>
          )}
          <Grid container spacing={2} pt={1}>
            {isCompany && <>
              <Grid item xs={12} md={12} pt={1}>
              <Typography fontWeight={600}>Site/CRO Details</Typography>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="company.name"
                value={values.company?.name}
                onChange={handleChange}
                label="Site/CRO name"
                size="small"
                error={touched.company?.name && Boolean(errors.company?.name)}
                helperText={touched.company?.name && errors.company?.name}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="company.taxId"
                value={values.company?.taxId}
                onChange={handleChange}
                label="Tax Id"
                size="small"
                error={touched.company?.taxId && Boolean(errors.company?.taxId)}
                helperText={touched.company?.taxId && errors.company?.taxId}
              />
            </Grid>
</>}
          
            <Grid item xs={12} md={12} pt={1}>
              <Typography fontWeight={600}>Representative Details</Typography>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.firstName"
                value={values.individual?.firstName}
                onChange={handleChange}
                label="First Name"
                size="small"
                error={touched.individual?.firstName && Boolean(errors.individual?.firstName)}
                helperText={touched.individual?.firstName && errors.individual?.firstName}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.lastName"
                value={values.individual?.lastName}
                onChange={handleChange}
                label="Last Name"
                size="small"
                error={touched.individual?.lastName && Boolean(errors.individual?.lastName)}
                helperText={touched.individual?.lastName && errors.individual?.lastName}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                type="email"
                name="individual.email"
                value={values.individual?.email}
                onChange={handleChange}
                label="Email"
                size="small"
                error={touched.individual?.email && Boolean(errors.individual?.email)}
                helperText={touched.individual?.email && errors.individual?.email}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <Field
                name="individual.phone"
                component={Phone}
                label="Contact Number"
                countries={['US']}
                defaultCountry="US"
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2} pt={0}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Birth"
                  disableFuture
                  value={values.individual?.dob || null}
                  onChange={(newValue) => {
                    setFieldValue('individual.dob', newValue);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: (touched.individual?.dob || submitCount > 0) && Boolean(errors.individual?.dob),
                      helperText: (touched.individual?.dob || submitCount > 0) && errors.individual?.dob,
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.id_number"
                value={values.individual?.id_number}
                onChange={handleChange}
                label="Social Security Number"
                size="small"
                sx={{pt:1}}
                error={touched.individual?.id_number && Boolean(errors.individual?.id_number)}
                helperText={touched.individual?.id_number && errors.individual?.id_number}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Typography fontWeight={600}>Address</Typography>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.address.line1"
                value={values.individual?.address?.line1}
                onChange={handleChange}
                label="Address Line 1"
                size="small"
                error={touched.individual?.address?.line1 && Boolean(errors.individual?.address?.line1)}
                helperText={touched.individual?.address?.line1 && errors.individual?.address?.line1}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.address.line2"
                value={values.individual?.address?.line2}
                onChange={handleChange}
                label="Address Line 2"
                size="small"
                error={touched.individual?.address?.line2 && Boolean(errors.individual?.address?.line2)}
                helperText={touched.individual?.address?.line2 && errors.individual?.address?.line2}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.address.city"
                value={values.individual?.address?.city}
                onChange={handleChange}
                label="City"
                size="small"
                error={touched.individual?.address?.city && Boolean(errors.individual?.address?.city)}
                helperText={touched.individual?.address?.city && errors.individual?.address?.city}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <FormControl
                fullWidth
                error={touched.individual?.address?.state && Boolean(errors.individual?.address?.state)}
                size="small"
              >
                <InputLabel id="individual.address.state-label">State</InputLabel>
                <Select
                  labelId="individual.address.state-label"
                  id="individual.address.state"
                  name="individual.address.state"
                  value={values.individual?.address?.state}
                  onChange={handleChange}
                  label="State"
                  renderValue={(value) => {
                    const state = getUsStateByCode(value);
                    return state ? formatUsStateOption(state) : value;
                  }}
                >
                  <MenuItem disabled value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select state</em>
                  </MenuItem>
                  {usStates.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {formatUsStateOption(option)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {touched.individual?.address?.state && errors.individual?.address?.state}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="individual.address.postalCode"
                value={values.individual?.address?.postalCode}
                onChange={handleChange}
                label="ZIP Code"
                placeholder="12345 or 12345-6789"
                size="small"
                inputProps={{ maxLength: 10 }}
                error={touched.individual?.address?.postalCode && Boolean(errors.individual?.address?.postalCode)}
                helperText={touched.individual?.address?.postalCode && errors.individual?.address?.postalCode}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="individual.address.country-label">Country</InputLabel>
                <Select
                  labelId="individual.address.country-label"
                  id="individual.address.country"
                  name="individual.address.country"
                  value={US_COUNTRY_CODE}
                  label="Country"
                  disabled
                  renderValue={() => formatCountryOption({ code: US_COUNTRY_CODE, name: 'United States' })}
                >
                  <MenuItem value={US_COUNTRY_CODE}>
                    {formatCountryOption({ code: US_COUNTRY_CODE, name: 'United States' })}
                  </MenuItem>
                </Select>
                <FormHelperText>US addresses only (territories and insular areas are not supported)</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}>
              <Typography fontWeight={600}>Bank Details</Typography>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="bankDetails.accountNumber"
                value={values.bankDetails?.accountNumber}
                onChange={handleChange}
                label="Account Number"
                size="small"
                error={touched.bankDetails?.accountNumber && Boolean(errors.bankDetails?.accountNumber)}
                helperText={touched.bankDetails?.accountNumber && errors.bankDetails?.accountNumber}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="bankDetails.confirmAccountNumber"
                value={values.bankDetails?.confirmAccountNumber}
                onChange={handleChange}
                label="Confirm Account Number"
                size="small"
                error={touched.bankDetails?.confirmAccountNumber && Boolean(errors.bankDetails?.confirmAccountNumber)}
                helperText={touched.bankDetails?.confirmAccountNumber && errors.bankDetails?.confirmAccountNumber}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="bankDetails.routingNumber"
                value={values.bankDetails?.routingNumber}
                onChange={handleChange}
                label="Routing Number"
                size="small"
                error={touched.bankDetails?.routingNumber && Boolean(errors.bankDetails?.routingNumber)}
                helperText={touched.bankDetails?.routingNumber && errors.bankDetails?.routingNumber}
              />
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <FormControl
                fullWidth
                error={touched.bankDetails?.accountHolderType && Boolean(errors.bankDetails?.accountHolderType)}
                size="small"
              >
                <InputLabel id="bankDetails.accountHolderType-label">Account Holder Type</InputLabel>
                <Select
                  labelId="bankDetails.accountHolderType-label"
                  id="bankDetails.accountHolderType"
                  name="bankDetails.accountHolderType"
                  value={values.bankDetails?.accountHolderType}
                  onChange={handleChange}
                  label="Account Holder Type"
                >
                  <MenuItem disabled value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select account holder type</em>
                  </MenuItem>
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="individual">Individual</MenuItem>
                </Select>
                <FormHelperText>
                  {touched.bankDetails?.accountHolderType && errors.bankDetails?.accountHolderType}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} pb={2}>
              <FormControl
                fullWidth
                error={touched.bankDetails?.country && Boolean(errors.bankDetails?.country)}
                size="small"
              >
                <InputLabel id="bankDetails.country-label">Bank Country</InputLabel>
                <Select
                  labelId="bankDetails.country-label"
                  id="bankDetails.country"
                  name="bankDetails.country"
                  value={values.bankDetails?.country}
                  onChange={(event) => {
                    const selectedCountry = bankCountries.find((country) => country.code === event.target.value);
                    setFieldValue('bankDetails.country', event.target.value);
                    setFieldValue('bankDetails.currency', selectedCountry?.currency);
                  }}
                  label="Bank Country"
                  renderValue={(value) => {
                    const country = getCountryByCode(value, bankCountries);
                    return country ? formatCountryOption(country) : value;
                  }}
                >
                  <MenuItem disabled value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select bank country</em>
                  </MenuItem>
                  {bankCountries.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {formatCountryOption(option)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{touched.bankDetails?.country && errors.bankDetails?.country}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                name="bankDetails.currency"
                value={values.bankDetails?.currency}
                // onChange={handleChange},
                disabled
                label="Currency"
                size="small"
                error={touched.bankDetails?.currency && Boolean(errors.bankDetails?.currency)}
                helperText={touched.bankDetails?.currency && errors.bankDetails?.currency}
              />
            </Grid>
             <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Credit 1"
                size="small"
              />
            </Grid>
              <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Debit 1"
                size="small"
              />
            </Grid>
             <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Credit 2"
                size="small"
              />
            </Grid>
              <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Debit 2"
                size="small"
              />
            </Grid>
             <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Credit 3"
                size="small"
              />
            </Grid>
              <Grid item xs={12} md={6} pb={2}>
              <TextField
                fullWidth
                label="Debit 3"
                size="small"
              />
            </Grid>
            <Grid item xs={12} display={'flex'} justifyContent={'center'} mb={0}>
              <Box paddingRight={1.5}>
                <CustomButton variant="outlined" onClick={() => handleClose()} disabled={isSubmitting || isLoading}>
                  Cancel
                </CustomButton>
              </Box>
              <Box>
                <CustomButton type="submit" variant="contained" disabled={isSubmitting || isLoading}>
                {isSubmitting || isLoading ? <CircularProgress size={25} style={{color:'white'}}/> : 'Submit'}
                </CustomButton>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
